"use client";

import { useTranslations } from "use-intl";

import { PhoneNumberInput } from "@/components/phone-number-input";
import { PhoneOtpActions } from "@/components/PhoneOtpActions";
import { VerificationCodeInput } from "@/components/verification-code-input";
import type { CountryCode } from "@garimpa/lib/countries";

import { Label } from "./ui/label";

interface PhoneOtpFormProps {
  mode: "signin" | "signup" | "link";
  country: CountryCode;
  phone: string;
  autoFocusPhone?: boolean;
  normalizedPhone: string | null;
  code: string;
  sending: boolean;
  verifying: boolean;
  resendSeconds: number;
  showPrimaryAction?: boolean;
  onCountryChange: (country: CountryCode) => void;
  onPhoneChange: (phone: string) => void;
  onCodeChange: (code: string) => void;
  onSendCode: () => Promise<void>;
  onVerifyCode: () => Promise<void>;
}

export function PhoneOtpForm(props: PhoneOtpFormProps) {
  const t = useTranslations("globals.auth");
  const hasSentCode = Boolean(props.normalizedPhone);
  const showPrimaryAction = props.showPrimaryAction !== false;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`phone-${props.mode}`}>{t("whatsappPhone")}</Label>
        <PhoneNumberInput
          id={`phone-${props.mode}`}
          country={props.country}
          number={props.phone}
          autoFocus={props.autoFocusPhone}
          disabled={hasSentCode}
          onCountryChange={props.onCountryChange}
          onNumberChange={props.onPhoneChange}
        />
      </div>

      {hasSentCode ? (
        <div className="space-y-1.5">
          <Label htmlFor={`phone-code-${props.mode}`}>{t("verificationCode")}</Label>
          <VerificationCodeInput
            id={`phone-code-${props.mode}`}
            value={props.code}
            ariaLabel={t("verificationCode")}
            autoFocus
            disabled={props.verifying}
            onChange={props.onCodeChange}
          />
          <p className="text-xs text-muted-foreground">{t("phoneCodeExpires")}</p>
        </div>
      ) : null}

      <PhoneOtpActions
        code={props.code}
        hasSentCode={hasSentCode}
        resendSeconds={props.resendSeconds}
        sending={props.sending}
        showPrimaryAction={showPrimaryAction}
        verifying={props.verifying}
        onSendCode={props.onSendCode}
        onVerifyCode={props.onVerifyCode}
      />
    </div>
  );
}
